import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AiFeedback, Feedback } from '../../../models/feedback';
import { ApiService } from '../../../core/api-service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { FeedbackService } from '../../../core/feedback.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MainRoutes } from '../../../core/enums';
import { Store } from '@ngrx/store';
import { selectChatSessionId } from '../../ai-insights/state/ai-insights-selectors';
import { concatLatestFrom } from '@ngrx/operators';
import { selectUserDetails } from '../../../store/global/core-selectors';
import { take } from 'rxjs/operators';

export const CATEGORIES: string[] = [
  'general',
  MainRoutes.HOME,
  MainRoutes.REVIEWS,
  MainRoutes.INSIGHTS,
  MainRoutes.AI_INSIGHTS,
  MainRoutes.USERS,
  MainRoutes.POSTS,
];

@Injectable()
export class UserFeedbackService {

  // best to worst; '&#x1F929;' = ecstatic smiley
  public readonly emoticonsList: string[] = ['&#x1F929;', '&#x1F642;', '&#x1F610;', '&#x1F641;'];

  public readonly feedbackForm = new FormGroup({
    rating: new FormControl<number | null>(null, [Validators.required]),
    comment: new FormControl<string | null>(null),
    category: new FormControl<string | null>(null),
  });

  private readonly loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public loading$ = this.loading.asObservable();

  constructor(private api: ApiService,
              private feedbackService: FeedbackService,
              private translate: TranslateService,
              private router: Router,
              private store: Store) {
    // set feature
    this.feedbackForm.get('category').setValue(this.getFeatureNameFromCurrentPath(
        this.router.routerState?.snapshot?.root?.firstChild?.url[0]?.path),
    );
  };

  /**
   * Submit form data - send feedback
   */
  sendFeedback() {
    let feedback$: Observable<any>;
    this.loading.next(true);

    if (this.feedbackForm.controls.category.value === MainRoutes.AI_INSIGHTS) {
      feedback$ = this.store.select(selectChatSessionId).pipe(
          concatLatestFrom(() => this.store.select(selectUserDetails)),
          take(1), // 1 time action
          switchMap(([sessionId, userDetails]) => {
            const feedbackBody: AiFeedback = {
              rating: this.feedbackForm.value.rating,
              comment: this.feedbackForm.value.comment,
              session_id: sessionId ? sessionId : '',
              username: userDetails.username,
            };

            return this.api.sendAiFeedback(feedbackBody);
          }),
      );
    } else {
      const feedbackBody: Feedback = {
        rating: this.feedbackForm.value.rating,
        comment: this.feedbackForm.value.comment,
        category: this.feedbackForm.value.category,
      };

      feedback$ = this.api.sendFeedback(feedbackBody);
    }

    feedback$
        .subscribe({
          next: () => {
            // reset form on success
            this.feedbackForm?.reset();
            this.loading.next(false);

            this.feedbackService.showSuccessSnackbar({
              data: { userFriendlyMessage: this.translate.instant('FEEDBACK.SUCCESS_INFO_MESSAGE') },
              duration: 5000,
            });
          }, error: (err: HttpErrorResponse) => {
            this.loading.next(false);

            this.feedbackService.showAlertSnackbar({
              data: { userFriendlyMessage: this.translate.instant('FEEDBACK.ERROR') },
            }, err?.status);
          },
        });
  }

  /**
   * Get feature name.
   * @param path
   */
  getFeatureNameFromCurrentPath(path: string) {
    return CATEGORIES.includes(path) ? path : 'general';
  }

  /**
   * Get rating from 1-4, where 1 = worst.
   * @param index
   */
  getRating(index: number) {
    return this.emoticonsList.length - index;
  }


}


